/* eslint-disable camelcase -- GTM dataLayer property names */
class PredictiveSearch extends HTMLElement {
  constructor () {
    super()
    this.cachedResults = {}
    this.input = this.querySelector('input[type="search"]')
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]')
    this.isOpen = false
    this.intentionalBlur = false
    this.searchSessionComplete = false
    this.noResultsReported = new Set()
    this.noResultsTimer = null
    this.setupEventListeners()
  }

  setupEventListeners () {
    const form = this.querySelector('form.search')
    form.addEventListener('submit', this.onFormSubmit.bind(this))
    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event)
    }, 300).bind(this))
    this.input.addEventListener('focus', this.onFocus.bind(this))
    this.input.addEventListener('blur', this.onBlur.bind(this))
    this.addEventListener('focusout', this.onFocusOut.bind(this))
    this.addEventListener('keyup', this.onKeyup.bind(this))
    this.addEventListener('keydown', this.onKeydown.bind(this))
    this.predictiveSearchResults.addEventListener('click', this.onResultsClick.bind(this))
    this.predictiveSearchResults.addEventListener('mousedown', this.onResultsMouseDown.bind(this))
    window.addEventListener('show:search-modal', this.onSearchModalShow.bind(this))
    window.addEventListener('hide:search-modal', this.onSearchModalHide.bind(this))
  }

  onSearchModalShow () {
    this.searchSessionComplete = false
    this.noResultsReported.clear()
    this.clearNoResultsTimer()
  }

  onSearchModalHide () {
    this.trackAbandon()
  }

  pushSearchEvent (searchAction, extra = {}) {
    if (typeof window === 'undefined' || !window.dataLayer) return
    window.dataLayer.push({
      event: 'site_search',
      search_action: searchAction,
      search_query: this.getQuery(),
      search_source: 'header',
      ...extra,
    })
  }

  clearNoResultsTimer () {
    if (this.noResultsTimer) {
      clearTimeout(this.noResultsTimer)
      this.noResultsTimer = null
    }
  }

  hasNoResults () {
    if (this.getAttribute('results') !== 'true') return false
    return this.querySelectorAll('.predictive-search__result-item').length === 0
  }

  scheduleNoResultsCheck () {
    this.clearNoResultsTimer()
    if (this.searchSessionComplete || !this.hasNoResults()) return

    const queryAtSchedule = this.getQuery()
    if (!queryAtSchedule.length) return

    this.noResultsTimer = setTimeout(() => {
      if (this.searchSessionComplete) return
      if (this.getQuery() !== queryAtSchedule) return
      if (!this.hasNoResults()) return

      const queryKey = queryAtSchedule.toLowerCase()
      if (this.noResultsReported.has(queryKey)) return

      this.noResultsReported.add(queryKey)
      this.pushSearchEvent('no_results', { search_query: queryAtSchedule })
    }, 3000)
  }

  getResultMetadata (link) {
    const item = link.closest('.predictive-search__result-item, .predictive-search__view-all-item, .predictive-search__custom-cta, .predictive-search__roof-bar-cta')
    if (!item) return {}

    if (item.classList.contains('predictive-search__view-all-item')) {
      return {
        search_result_type: 'view_all',
        search_result_title: link.textContent.trim(),
        search_result_url: link.getAttribute('href') || '',
      }
    }

    if (item.classList.contains('predictive-search__custom-cta') || item.classList.contains('predictive-search__roof-bar-cta')) {
      return {
        search_result_type: 'cta',
        search_result_title: item.querySelector('h3')?.textContent.trim() || link.textContent.trim(),
        search_result_url: link.getAttribute('href') || '',
      }
    }

    let searchResultType = 'product'
    if (item.classList.contains('predictive-search__result-item--page')) searchResultType = 'page'
    if (item.classList.contains('predictive-search__result-item--article')) searchResultType = 'article'

    const titleEl = item.querySelector('.predictive-search__result-title')
    const items = this.querySelectorAll('.predictive-search__result-item')
    let searchResultPosition = 0
    items.forEach((el, index) => {
      if (el === item) searchResultPosition = index + 1
    })

    return {
      search_result_type: searchResultType,
      search_result_title: titleEl?.textContent.trim() || link.textContent.trim(),
      search_result_url: link.getAttribute('href') || '',
      search_result_position: searchResultPosition || undefined,
    }
  }

  onResultsMouseDown (event) {
    const link = event.target.closest('a[href]')
    if (!link || !this.predictiveSearchResults.contains(link)) return
    this.searchSessionComplete = true
    this.clearNoResultsTimer()
  }

  onResultsClick (event) {
    const link = event.target.closest('a[href]')
    if (!link || !this.predictiveSearchResults.contains(link)) return

    const href = link.getAttribute('href')
    if (!href) return

    const metadata = this.getResultMetadata(link)
    const payload = {
      event: 'site_search',
      search_action: 'click',
      search_query: this.getQuery(),
      search_source: 'header',
      ...metadata,
    }

    this.searchSessionComplete = true
    this.clearNoResultsTimer()

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) {
      this.pushSearchEvent('click', metadata)
      return
    }

    event.preventDefault()

    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push(payload)
      window.addEventListener('beforeunload', () => {
        window.dataLayer.push(payload)
      }, { once: true })
    }

    setTimeout(() => {
      window.location.assign(href)
    }, 150)
  }

  trackAbandon () {
    if (this.searchSessionComplete) return

    const query = this.getQuery()
    if (!query.length) return

    this.searchSessionComplete = true
    this.clearNoResultsTimer()
    this.pushSearchEvent('abandon', { search_query: query })
  }

  getQuery () {
    return this.input.value.trim()
  }

  onChange () {
    this.clearNoResultsTimer()
    const searchTerm = this.getQuery()
    if (!searchTerm.length) {
      this.close(true)
      return
    }
    this.getSearchResults(searchTerm)
  }

  onFormSubmit (event) {
    const query = this.getQuery()
    const hasSelectedResult = this.querySelector('[aria-selected="true"] a')

    if (!query.length || hasSelectedResult) {
      event.preventDefault()
      return
    }

    this.searchSessionComplete = true
    this.clearNoResultsTimer()
    this.pushSearchEvent('submit', { search_query: query })
  }

  onFocus () {
    const searchTerm = this.getQuery()
    if (!searchTerm.length) return
    if (this.getAttribute('results') === 'true') {
      this.open()
    } else {
      this.getSearchResults(searchTerm)
    }
  }

  onBlur () {
    if (window.innerWidth < 768) {
      this.input.blur()
    }
  }

  onFocusOut () {
    setTimeout(() => {
      if (!this.contains(document.activeElement) && !this.intentionalBlur) {
        this.close()
      }
    })
  }

  onKeyup (event) {
    if (!this.getQuery().length) this.close(true)
    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault()
        this.switchOption('up')
        break
      case 'ArrowDown':
        event.preventDefault()
        this.switchOption('down')
        break
      case 'Enter':
        event.preventDefault()
        if (this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')) {
          this.selectOption()
        } else if (this.getQuery().length) {
          this.querySelector('form.search')?.requestSubmit()
        }
        break
    }
  }

  onKeydown (event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault()
    }
  }

  onTouchStart (event) {
    if (this.getAttribute('open') === 'true' && window.innerWidth < 768) {
      const searchModal = document.getElementById('tb_header_menu_search_modal')
      if (searchModal && !searchModal.contains(event.target)) {
        this.close()
      }
    }
  }

  onResultsScroll () {
    if (window.innerWidth < 768 && this.input === document.activeElement) {
      this.intentionalBlur = true
      this.input.blur()
      setTimeout(() => {
        this.intentionalBlur = false
      }, 100)
    }
  }

  switchOption (direction) {
    if (!this.getAttribute('open')) return
    const moveUp = direction === 'up'
    const selectedElement = this.querySelector('[aria-selected="true"]')
    const allElements = this.querySelectorAll('.predictive-search__result-item, .predictive-search__view-all-item, .predictive-search__roof-bar-cta-button, .predictive-search__custom-cta-button')
    let activeElement = this.querySelector('.predictive-search__result-item, .predictive-search__view-all-item, .predictive-search__roof-bar-cta-button, .predictive-search__custom-cta-button')
    if (moveUp && !selectedElement) return
    this.statusElement.textContent = ''
    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0]
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1]
    }
    if (activeElement === selectedElement) return
    activeElement.setAttribute('aria-selected', true)
    if (selectedElement) selectedElement.setAttribute('aria-selected', false)
    this.setLiveRegionText(activeElement.textContent)
    this.input.setAttribute('aria-activedescendant', activeElement.id)
  }

  selectOption () {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')
    if (selectedProduct) selectedProduct.click()
  }

  getSearchResults (searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase()
    this.setLiveRegionLoadingState()
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    const basePath = (window?.Shopify?.routes?.root || '/')
    fetch(`${basePath}search/suggest?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,page,article&${encodeURIComponent('resources[limit]')}=4&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status)
          this.close()
          throw error
        }
        return response.text()
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML
        this.cachedResults[queryKey] = resultsMarkup
        this.renderSearchResults(resultsMarkup)
      })
      .catch((error) => {
        this.close()
        throw error
      })
  }

  setLiveRegionLoadingState () {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status')
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text')
    this.setLiveRegionText(this.loadingText)
    this.setAttribute('loading', true)
  }

  setLiveRegionText (statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false')
    this.statusElement.textContent = statusText
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true')
    }, 1000)
  }

  renderSearchResults (resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup
    this.setAttribute('results', true)
    this.setLiveRegionResults()
    this.open()
    this.scheduleNoResultsCheck()
  }

  setLiveRegionResults () {
    this.removeAttribute('loading')
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent)
  }

  getResultsMaxHeight () {
    const header = document.getElementById('shopify-section-header')
    const searchForm = this.querySelector('form.search')
    const headerHeight = header ? header.getBoundingClientRect().height : 60
    const formHeight = searchForm ? searchForm.offsetHeight : 80
    const availableHeight = window.innerHeight * (window.innerWidth < 768 ? 0.9 : 1)
    this.resultsMaxHeight = availableHeight - headerHeight - formHeight
    return Math.max(this.resultsMaxHeight, 200)
  }

  open () {
    const scrollableContainer = this.querySelector('#predictive-search-results')
    if (scrollableContainer) {
      scrollableContainer.style.maxHeight = `${this.getResultsMaxHeight()}px`
    }
    if (window.innerWidth < 768) {
      this.scrollY = window.scrollY
      document.body.classList.add('overflow-hidden', 'fixed', 'w-full')
      document.body.style.top = `-${this.scrollY}px`
    }
    this.setAttribute('open', true)
    this.input.setAttribute('aria-expanded', true)
    if (window.innerWidth < 768) {
      this.boundTouchStart = this.onTouchStart.bind(this)
      document.addEventListener('touchstart', this.boundTouchStart)
      const scrollableContainer = this.querySelector('#predictive-search-results')
      if (scrollableContainer) {
        this.boundScrollHandler = this.onResultsScroll.bind(this)
        scrollableContainer.addEventListener('scroll', this.boundScrollHandler)
      }
    }
    this.isOpen = true
  }

  close (clearSearchTerm = false) {
    if (!clearSearchTerm) this.trackAbandon()

    if (clearSearchTerm) {
      this.input.value = ''
      this.removeAttribute('results')
      this.clearNoResultsTimer()
    }
    const selected = this.querySelector('[aria-selected="true"]')
    if (selected) selected.setAttribute('aria-selected', false)
    this.input.setAttribute('aria-activedescendant', '')
    this.removeAttribute('open')
    this.input.setAttribute('aria-expanded', false)
    this.resultsMaxHeight = false
    document.body.classList.remove('overflow-hidden', 'fixed', 'w-full')
    document.body.style.top = ''
    if (this.scrollY) {
      window.scrollTo(0, this.scrollY)
      this.scrollY = null
    }
    if (this.boundTouchStart) {
      document.removeEventListener('touchstart', this.boundTouchStart)
      this.boundTouchStart = null
    }
    if (this.boundScrollHandler) {
      const scrollableContainer = this.querySelector('#predictive-search-results')
      if (scrollableContainer) {
        scrollableContainer.removeEventListener('scroll', this.boundScrollHandler)
      }
      this.boundScrollHandler = null
    }
    const scrollableContainer = this.querySelector('#predictive-search-results')
    if (scrollableContainer) {
      scrollableContainer.removeAttribute('style')
    }
    this.isOpen = false
  }
}
customElements.define('predictive-search', PredictiveSearch)
